import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useATSAuth } from './ATSAuthContext';

const ATSDataContext = createContext(null);

export const useATSData = () => {
  const context = useContext(ATSDataContext);
  if (!context) {
    throw new Error('useATSData must be used within ATSDataProvider');
  }
  return context;
};

export const ATSDataProvider = ({ children }) => {
  const { api, token } = useATSAuth();
  const [lovData, setLovData] = useState({});
  const [loading, setLoading] = useState(false);
  
  // Dropdown data
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [requirements, setRequirements] = useState([]);

  const fetchLOVData = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const response = await api.get('/api/ats/lov/all');
      setLovData(response.data);
    } catch (error) {
      console.error('Failed to fetch LOV data:', error);
    } finally {
      setLoading(false);
    }
  }, [api, token]);

  const fetchDropdownData = useCallback(async () => {
    if (!token) return;

    try {
      const [empRes, clientRes, vendorRes, reqRes] = await Promise.all([
        api.get('/api/ats/employees/dropdown'),
        api.get('/api/ats/clients/dropdown'),
        api.get('/api/ats/vendors/dropdown'),
        api.get('/api/ats/requirements/dropdown')
      ]);
      
      setEmployees(empRes.data);
      setClients(clientRes.data);
      setVendors(vendorRes.data);
      setRequirements(reqRes.data);
    } catch (error) {
      console.error('Failed to fetch dropdown data:', error);
    }
  }, [api, token]);

  useEffect(() => {
    if (token) {
      fetchLOVData();
      fetchDropdownData();
    }
  }, [token, fetchLOVData, fetchDropdownData]);

  const refreshDropdowns = useCallback(() => {
    fetchDropdownData();
  }, [fetchDropdownData]);

  // Helper to get LOV label by id
  const getLovLabel = useCallback((lovName, id) => {
    const lovList = lovData[lovName] || [];
    const item = lovList.find(l => l.id === id);
    return item?.value || '-';
  }, [lovData]);

  const value = {
    lovData,
    loading,
    employees,
    clients,
    vendors,
    requirements,
    getLovLabel,
    refreshDropdowns,
    fetchLOVData
  };

  return (
    <ATSDataContext.Provider value={value}>
      {children}
    </ATSDataContext.Provider>
  );
};

export default ATSDataContext;
